import React from 'react';

/**
 * Avatar Styles
 *
 * Reusable avatar and photo display components for consistent user image presentation.
 *
 * Usage Philosophy:
 * - Pre-composed styles for common avatar patterns (90% of use cases)
 * - Building blocks for custom avatar compositions (10% edge cases)
 * - Supports both photo display and initial-based fallbacks
 * - Responsive sizing with mobile-friendly defaults
 *
 * Common Patterns:
 * - User profiles: Use avatarStyles.default or avatarStyles.large
 * - Student grids: Use avatarStyles.square or avatarStyles.compact
 * - List items: Use avatarStyles.small or avatarStyles.circular
 * - Fallback initials: Use avatarInitials styles when no photo available
 */

// ============================================================================
// PRE-COMPOSED AVATAR STYLES (90% of use cases)
// ============================================================================

export const avatarStyles = {
  /**
   * Default Avatar - Square with 1:1 aspect ratio
   * Use for: Student photos, profile images, cards
   */
  default: {
    width: '100%',
    aspectRatio: '1',
    borderRadius: 'var(--radius)',
    overflow: 'hidden',
    backgroundColor: 'var(--surface-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,

  /**
   * Circular Avatar - Round profile image
   * Use for: User menu, comments, activity feeds
   */
  circular: {
    width: '100%',
    aspectRatio: '1',
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: 'var(--surface-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,

  /**
   * Square Avatar - Fixed aspect ratio with border
   * Use for: Grid layouts, directory listings
   */
  square: {
    width: '100%',
    aspectRatio: '1',
    borderRadius: 'var(--radius)',
    overflow: 'hidden',
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,

  /**
   * Small Avatar - Compact size for lists
   * Use for: Dropdown menus, compact lists, inline mentions
   */
  small: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: 'var(--surface-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,

  /**
   * Medium Avatar - Standard size
   * Use for: User profiles, cards, modals
   */
  medium: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: 'var(--surface-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,

  /**
   * Medium-Large Avatar - 60px display
   * Use for: Modal headers, report cards
   */
  mediumLarge: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: 'var(--surface-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,

  /**
   * Large Avatar - Prominent display
   * Use for: Profile pages, detail views
   */
  large: {
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: 'var(--surface-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,

  /**
   * Compact Square - Smaller square avatar
   * Use for: Dense grids, thumbnails
   */
  compact: {
    width: '100%',
    aspectRatio: '1',
    borderRadius: 'calc(var(--radius) * 0.5)',
    overflow: 'hidden',
    backgroundColor: 'var(--surface-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
};

// ============================================================================
// AVATAR IMAGE STYLES
// ============================================================================

export const avatarImage = {
  /**
   * Standard Avatar Image - Full size, cover fit
   * Use for: Photo display within avatar container
   */
  default: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    objectPosition: 'center 10%', // Show more of the top (head) instead of centering
  } as React.CSSProperties,

  /**
   * Contain Avatar Image - Preserve aspect ratio
   * Use for: Logos, illustrations that shouldn't be cropped
   */
  contain: {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    padding: '0.5rem',
  } as React.CSSProperties,
};

// ============================================================================
// AVATAR INITIALS (Fallback when no photo)
// ============================================================================

export const avatarInitials = {
  /**
   * Default Initials - Standard size
   * Use for: Fallback display when photo is unavailable
   */
  default: {
    fontSize: '1.5rem',
    color: 'var(--text-light)',
    fontWeight: '600',
    userSelect: 'none' as const,
  } as React.CSSProperties,

  /**
   * Small Initials - Compact size
   * Use for: Small avatar containers
   */
  small: {
    fontSize: '0.75rem',
    color: 'var(--text-light)',
    fontWeight: '600',
    userSelect: 'none' as const,
  } as React.CSSProperties,

  /**
   * Large Initials - Prominent size
   * Use for: Large avatar containers
   */
  large: {
    fontSize: '2.5rem',
    color: 'var(--text-light)',
    fontWeight: '600',
    userSelect: 'none' as const,
  } as React.CSSProperties,

  /**
   * Responsive Initials - Mobile-aware sizing
   * Use for: Responsive grids with mobile/desktop differences
   */
  responsive: (isMobile: boolean) =>
    ({
      fontSize: isMobile ? '2rem' : '1.5rem',
      color: 'var(--text-light)',
      fontWeight: '600',
      userSelect: 'none' as const,
    }) as React.CSSProperties,
};

// ============================================================================
// BUILDING BLOCKS (10% edge cases)
// ============================================================================

export const avatarBase = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  backgroundColor: 'var(--surface-color)',
} as React.CSSProperties;

export const avatarSizes = {
  xs: { width: '24px', height: '24px' },
  sm: { width: '32px', height: '32px' },
  md: { width: '48px', height: '48px' },
  mdLg: { width: '60px', height: '60px' },
  lg: { width: '64px', height: '64px' },
  xl: { width: '96px', height: '96px' },
  '2xl': { width: '128px', height: '128px' },
};

export const avatarShapes = {
  circle: { borderRadius: '50%' },
  rounded: { borderRadius: 'var(--radius)' },
  square: { borderRadius: '0' },
};

export const avatarBorders = {
  none: { border: 'none' },
  default: { border: '1px solid var(--border-color)' },
  thick: { border: '2px solid var(--border-color)' },
  primary: { border: '2px solid var(--primary-color)' },
  success: { border: '2px solid var(--success-color)' },
  warning: { border: '2px solid var(--warning-color)' },
  error: { border: '2px solid var(--error-color)' },
};

// ============================================================================
// AVATAR GROUPS (Multiple avatars stacked)
// ============================================================================

export const avatarGroup = {
  /**
   * Avatar Group Container - Horizontally stacked avatars
   * Use for: Showing multiple users (e.g., "3 people in this class")
   */
  container: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '0',
  } as React.CSSProperties,

  /**
   * Avatar Group Item - Individual avatar in group with overlap
   * Use for: Each avatar within avatar group
   */
  item: {
    marginLeft: '-0.5rem',
    border: '2px solid var(--background-color)',
    borderRadius: '50%',
  } as React.CSSProperties,

  /**
   * Avatar Group First Item - No overlap
   * Use for: First avatar in group (no negative margin)
   */
  firstItem: {
    marginLeft: '0',
    border: '2px solid var(--background-color)',
    borderRadius: '50%',
  } as React.CSSProperties,
};

// ============================================================================
// AVATAR STATUS INDICATORS
// ============================================================================

export const avatarStatus = {
  /**
   * Online Status Badge - Green dot
   * Use for: Indicating user is online/active
   */
  online: {
    position: 'absolute' as const,
    bottom: '0',
    right: '0',
    width: '12px',
    height: '12px',
    backgroundColor: 'var(--success-color)',
    border: '2px solid var(--background-color)',
    borderRadius: '50%',
  } as React.CSSProperties,

  /**
   * Offline Status Badge - Gray dot
   * Use for: Indicating user is offline
   */
  offline: {
    position: 'absolute' as const,
    bottom: '0',
    right: '0',
    width: '12px',
    height: '12px',
    backgroundColor: 'var(--text-secondary)',
    border: '2px solid var(--background-color)',
    borderRadius: '50%',
  } as React.CSSProperties,

  /**
   * Away Status Badge - Yellow dot
   * Use for: Indicating user is away/idle
   */
  away: {
    position: 'absolute' as const,
    bottom: '0',
    right: '0',
    width: '12px',
    height: '12px',
    backgroundColor: 'var(--warning-color)',
    border: '2px solid var(--background-color)',
    borderRadius: '50%',
  } as React.CSSProperties,
};
